import { Autocomplete, Box, InputLabel, TextField, Typography } from '@mui/material';
import { Controller, FieldValues } from 'react-hook-form';
import useSWR from 'swr';

import { User } from '../../Types/AuthServer';
import AvatarImageWrapper from '../Avatars/AvatarImageWrapper';
import { UploadType } from '../Image/UploadType';
import { BaseInputProps } from './GetFormInput';

export function FormSelectUser<T extends FieldValues>({
    control,
    disabled,
    fullWidth,
    helperText,
    label,
    options,
    required,
    small,
    value,
}: Readonly<BaseInputProps<T>>) {
    const { data: userList = [] } = useSWR<User[]>('users/usermanage/');
    const userOptions = userList.map((user) => ({
        id: user.id,
        image: user.image,
        label: `${user.first_name} ${user.last_name}`,
    }));

    return (
        <Controller
            control={control}
            name={value}
            render={({ field: { onBlur, onChange, ref, value: inputValue }, fieldState: { error } }) => {
                return (
                    <Box>
                        <InputLabel sx={{ color: 'black', fontWeight: 'medium', mb: 0.5 }}>{label}</InputLabel>
                        <Autocomplete
                            disabled={disabled}
                            getOptionLabel={(option) => option.label}
                            isOptionEqualToValue={(option) => option.id === inputValue}
                            multiple
                            onBlur={onBlur}
                            onChange={(e, options) => onChange(options.map((option) => option.id))}
                            options={userOptions ?? []}
                            ref={ref}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    error={Boolean(error?.message)}
                                    helperText={error?.message ?? helperText}
                                    required={required}
                                    variant="outlined"
                                />
                            )}
                            renderOption={(props, option) => (
                                <Typography {...props}>
                                    <AvatarImageWrapper
                                        imagePath={option.image}
                                        imageType={UploadType.Profile}
                                        isLoading={false}
                                        sx={{
                                            height: 40,
                                            mr: 1,
                                            width: 40,
                                        }}
                                    />
                                    {option.label}
                                </Typography>
                            )}
                            size={small ? 'small' : 'medium'}
                            sx={{ width: fullWidth ? undefined : 350 }}
                        />
                    </Box>
                );
            }}
            rules={{
                required: required ? `${label ?? value} cannot be empty` : false,
            }}
        />
    );
}
